import type { LucideIcon } from "lucide-react";
import { StatusBadge, type StatusTone } from "@/components/status-badge";
import { cn } from "@/lib/cn";

/**
 * Canonical Badge — wraps the existing StatusBadge so new code reaches
 * for the same `Badge` primitive that other component libraries use.
 *
 * The visual is identical to StatusBadge (10.5px, rounded-full, tinted
 * border + bg). Severity / state aliases are added so callers can pass
 * domain-level vocabulary instead of remapping every priority/status
 * value to a tone:
 *
 *   <Badge severity="critical">Critical</Badge>
 *   <Badge state="approved">Approved</Badge>
 *   <Badge tone="info" icon={InfoIcon}>Note</Badge>
 *
 * Existing call sites that already use StatusBadge keep working — they
 * import the same component under its original name.
 */
export type BadgeSeverity = "critical" | "high" | "medium" | "low" | "info";
export type BadgeState =
  | "draft" | "review" | "approved" | "rejected" | "blocked"
  | "ready" | "running" | "done" | "failed" | "skipped";

const SEVERITY_TONE: Record<BadgeSeverity, StatusTone> = {
  critical: "danger",
  high:     "warning",
  medium:   "warning",
  low:      "success",
  info:     "info",
};

const STATE_TONE: Record<BadgeState, StatusTone> = {
  draft:    "muted",
  review:   "warning",
  approved: "success",
  rejected: "danger",
  blocked:  "danger",
  ready:    "info",
  running:  "info",
  done:     "success",
  failed:   "danger",
  skipped:  "muted",
};

export type BadgeProps = {
  tone?: StatusTone;
  /** Domain alias — maps to `tone`. Wins over `tone` when both are set. */
  severity?: BadgeSeverity;
  /** Domain alias — maps to `tone`. Wins over `tone` when both are set. */
  state?: BadgeState;
  /** Optional leading dot indicator (instead of an icon). */
  dot?: boolean;
  icon?: LucideIcon;
  className?: string;
  children: React.ReactNode;
};

export function Badge({
  tone,
  severity,
  state,
  dot,
  icon,
  className,
  children,
}: BadgeProps) {
  const resolvedTone: StatusTone =
    severity ? SEVERITY_TONE[severity] :
    state    ? STATE_TONE[state] :
    tone     ?? "neutral";

  return (
    <StatusBadge tone={resolvedTone} icon={icon} className={cn(dot && "pl-1.5", className)}>
      {dot && (
        <span
          aria-hidden
          className={cn(
            "h-1.5 w-1.5 rounded-full -ml-0.5 mr-0.5",
            // Match the foreground colour the badge already paints — the
            // bg-current trick keeps the dot in lock-step with the tone.
            "bg-current opacity-80",
          )}
        />
      )}
      {children}
    </StatusBadge>
  );
}

export type { StatusTone } from "@/components/status-badge";
